import type { DocumentShareCredentials } from '@/entities/clinicalDocument/documentShareCredentials'
import { supabase } from '@/shared/supabase/client'
import { supabaseConfig } from '@/shared/config/supabase'
import { AppError } from '@/shared/lib/appError'

export interface ExistingShareTokenState {
  /** Cleartext do link ativo. */
  credentials: DocumentShareCredentials | null
  /** Há token ativo no servidor (hash persistido; cleartext pode estar indisponível). */
  hasActiveLink: boolean
  /** Há token revogado e nenhum ativo: dá para habilitar o mesmo endereço e o mesmo código. */
  canEnableLink: boolean
}

type ShareTokenRow = {
  id?: string
  share_token?: string | null
  unlock_code?: string | null
  revoked_at?: string | null
  created_at?: string | null
}

const credentialsFromRow = (row: ShareTokenRow | undefined): DocumentShareCredentials | null => {
  const storedToken = typeof row?.share_token === 'string' ? row.share_token.trim() : ''
  const storedUnlock = typeof row?.unlock_code === 'string' ? row.unlock_code.trim() : ''
  if (!storedToken || !storedUnlock) return null
  return { token: storedToken, unlockCode: storedUnlock }
}

const mintedThisSession = new Map<string, DocumentShareCredentials>()
const SHARE_CREDS_STORAGE_KEY = 'synap-document-share-creds'

const readStoredShareCredentials = (clinicalDocumentId: string): DocumentShareCredentials | null => {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(SHARE_CREDS_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, DocumentShareCredentials>
    const creds = parsed[clinicalDocumentId]
    if (!creds?.token?.trim() || !creds.unlockCode?.trim()) return null
    return { token: creds.token.trim(), unlockCode: creds.unlockCode.trim() }
  } catch {
    return null
  }
}

const writeStoredShareCredentials = (clinicalDocumentId: string, creds: DocumentShareCredentials) => {
  if (typeof localStorage === 'undefined') return
  try {
    const raw = localStorage.getItem(SHARE_CREDS_STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as Record<string, DocumentShareCredentials>) : {}
    parsed[clinicalDocumentId] = creds
    localStorage.setItem(SHARE_CREDS_STORAGE_KEY, JSON.stringify(parsed))
  } catch {
    /* armazenamento local cheio ou bloqueado — a sessão em memória segue valendo */
  }
}

const clearStoredShareCredentials = (clinicalDocumentId: string) => {
  if (typeof localStorage === 'undefined') return
  try {
    const raw = localStorage.getItem(SHARE_CREDS_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Record<string, DocumentShareCredentials>
    delete parsed[clinicalDocumentId]
    localStorage.setItem(SHARE_CREDS_STORAGE_KEY, JSON.stringify(parsed))
  } catch {
    /* ignora */
  }
}

const refreshAccessToken = async (): Promise<string | null> => {
  const { data: sessionData } = await supabase.auth.getSession()
  const session = sessionData?.session
  if (session?.refresh_token) {
    const { data: refreshed } = await supabase.auth.refreshSession()
    return refreshed?.session?.access_token ?? session.access_token ?? null
  }
  return session?.access_token ?? null
}

export const clinicalDocumentShareTokensRepository = {
  getMintedThisSession(clinicalDocumentId: string): DocumentShareCredentials | null {
    const inMemory = mintedThisSession.get(clinicalDocumentId) ?? null
    if (inMemory) return inMemory
    const stored = readStoredShareCredentials(clinicalDocumentId)
    if (stored) mintedThisSession.set(clinicalDocumentId, stored)
    return stored
  },

  rememberMinted(clinicalDocumentId: string, creds: DocumentShareCredentials): void {
    mintedThisSession.set(clinicalDocumentId, creds)
    writeStoredShareCredentials(clinicalDocumentId, creds)
  },

  clearSessionCache(clinicalDocumentId: string): void {
    mintedThisSession.delete(clinicalDocumentId)
    clearStoredShareCredentials(clinicalDocumentId)
  },

  /** Consulta o link deste documento. Não cria outro endereço. */
  async getExisting(clinicalDocumentId: string): Promise<ExistingShareTokenState> {
    const { data, error } = await supabase
      .from('clinical_document_share_tokens')
      .select('id, share_token, unlock_code, revoked_at, created_at')
      .eq('clinical_document_id', clinicalDocumentId)
      .order('created_at', { ascending: true })
    if (error) {
      throw new AppError(error.message || 'Não foi possível verificar o link.', { status: 400 })
    }
    const rows = (data ?? []) as ShareTokenRow[]
    const active = rows.find((row) => !row.revoked_at)
    const storedCredentials = credentialsFromRow(active)
    if (storedCredentials) this.rememberMinted(clinicalDocumentId, storedCredentials)
    return {
      credentials: storedCredentials,
      hasActiveLink: Boolean(active),
      canEnableLink: !active && rows.length > 0,
    }
  },

  /**
   * Reativa o primeiro link do documento. O endereço e o código de 4 dígitos não mudam.
   */
  async enable(clinicalDocumentId: string): Promise<DocumentShareCredentials> {
    const { data, error } = await supabase
      .from('clinical_document_share_tokens')
      .select('id, share_token, unlock_code, revoked_at, created_at')
      .eq('clinical_document_id', clinicalDocumentId)
      .order('created_at', { ascending: true })
    if (error) {
      throw new AppError(error.message || 'Não foi possível habilitar o link.', { status: 400 })
    }
    const rows = (data ?? []) as ShareTokenRow[]
    const active = rows.find((row) => !row.revoked_at)
    const activeCredentials = credentialsFromRow(active)
    if (active && activeCredentials) {
      this.rememberMinted(clinicalDocumentId, activeCredentials)
      return activeCredentials
    }
    const canonical = rows.find((row) => credentialsFromRow(row)) ?? rows[0]
    if (!canonical?.id) {
      throw new AppError('Não há link para habilitar.', { status: 404 })
    }
    const { error: updateError } = await supabase
      .from('clinical_document_share_tokens')
      .update({ revoked_at: null })
      .eq('id', canonical.id)
    if (updateError) {
      throw new AppError(updateError.message || 'Não foi possível habilitar o link.', { status: 400 })
    }
    const credentials = credentialsFromRow(canonical)
    if (!credentials) {
      throw new AppError('O link foi habilitado, mas o código não está disponível neste aparelho.', { status: 409 })
    }
    this.rememberMinted(clinicalDocumentId, credentials)
    return credentials
  },

  /**
   * Gera um novo par token/código na edge — apenas em ações explícitas (ex.: botão **Compartilhar**, fluxos que pedem link público).
   * Não chamar ao “apenas imprimir” ou ao abrir PDF sem essa intenção.
   */
  async mint(clinicalDocumentId: string, expiresInDays?: number | null): Promise<DocumentShareCredentials> {
    const accessToken = await refreshAccessToken()
    if (!accessToken) {
      throw new AppError('Sessão expirada. Faça login novamente.', { status: 401, code: 'UNAUTHORIZED' })
    }

    const body: { clinicalDocumentId: string; expiresInDays?: number } = { clinicalDocumentId }
    if (expiresInDays != null && Number.isFinite(expiresInDays)) {
      body.expiresInDays = expiresInDays
    }

    const url = `${supabaseConfig.url.replace(/\/$/, '')}/functions/v1/mint-document-share-token`
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        apikey: supabaseConfig.anonKey,
      },
      body: JSON.stringify(body),
    })

    const text = await res.text()
    type MintResponse = { success?: boolean; shareToken?: string; unlockCode?: string; error?: string }
    let parsed: MintResponse | null = null
    try {
      parsed = text ? (JSON.parse(text) as MintResponse) : null
    } catch {
      parsed = null
    }

    if (!res.ok) {
      const msg =
        (parsed && typeof parsed.error === 'string' && parsed.error) ||
        text?.trim() ||
        res.statusText ||
        `Erro ${res.status}`
      throw new AppError(msg, { status: res.status, code: res.status === 409 ? 'CONFLICT' : 'UNKNOWN' })
    }

    if (!parsed || parsed.success !== true || typeof parsed.shareToken !== 'string' || typeof parsed.unlockCode !== 'string') {
      const msg = parsed && typeof parsed.error === 'string' ? parsed.error : 'Resposta inválida ao gerar link.'
      throw new AppError(msg, { status: 500 })
    }

    const creds: DocumentShareCredentials = { token: parsed.shareToken, unlockCode: parsed.unlockCode }
    this.rememberMinted(clinicalDocumentId, creds)
    return creds
  },

  async revoke(clinicalDocumentId: string): Promise<void> {
    const { error } = await supabase.rpc('clinical_document_share_token_revoke', {
      p_clinical_document_id: clinicalDocumentId,
    })
    if (error) {
      throw new AppError(error.message || 'Não foi possível revogar o link.', { status: 400 })
    }
    mintedThisSession.delete(clinicalDocumentId)
  },
}
